/**
 * Champion ("legend" in code) wire types — docs/champion-system-design.md.
 *
 * The UI says "Champion"; the sim, intents and snapshot say `legend`.
 * Everything here is additive and only present while the `legends.enabled`
 * ruleset module is on (absent = not a champion / no legend state).
 * Mirrors `packages/sim-wasm/src/intents/legend.rs` and
 * `packages/sim-wasm/src/snapshot/legend.rs`.
 */

/** Append-only (mirrors `LegendKind` discriminants 0, 1, 2); Phase B adds golem and lich. */
export const LEGEND_KINDS = ['dragon', 'golem', 'lich'] as const;
export type LegendKind = (typeof LEGEND_KINDS)[number];

export const LEGEND_STATES = ['dormant', 'summoning', 'alive', 'fallen'] as const;
export type LegendStateName = (typeof LEGEND_STATES)[number];

/** Player global `legend` (snapshot `state.globals.players[i].legend`). */
export interface LegendPlayerState {
  /** Player mana pools (null when the player has none, e.g. magic off). */
  mana: { radiant: number; nether: number } | null;
  state: LegendStateName;
  /** Fixed at the first summon; null before. */
  kind: LegendKind | null;
  /** Times the champion has died. */
  deaths: number;
  /** 1..5 */
  level: number;
  kills: number;
  /** 0..100, Lich only (always 0 for the Dragon). */
  soul: number;
  /** `summoning`: tick the project completes; otherwise null. */
  doneTick: number | null;
  /** `summoning`: the city running the project (`city_<n>`); otherwise null. */
  cityId: string | null;
  /** `alive`: the creature's unit node id (`unit_<n>`); otherwise null. */
  unitId: string | null;
  /** `fallen`: tick the champion may be re-summoned; otherwise null. */
  respawnTick: number | null;
  /**
   * Phase B (additive): the kind a summon would produce now - the fixed kind
   * once chosen, else the dominant doctrine's default (no choice screen).
   * null when none is offered (doctrine tier not reached).
   */
  offered?: LegendKind | null;
  /** Phase B (additive), Lich only: the summon city whose loss kills it (`city_<n>`). */
  phylacteryCityId?: string | null;
}

/** Status kinds on `statuses` (append-only; burn / chill / root are reserved). */
export type UnitStatusKind = 'wither' | 'fortify' | 'garrison' | 'ascended';

/**
 * Unit prop `statuses` on ANY unit (absent = none): active `CLegendStatus`
 * entries, kind-sorted. `untilTick` is null for statuses that never expire
 * (fortify / garrison / ascended); `mag` is the integer magnitude (wither =
 * attack-roll penalty, ascended = attack-roll bonus).
 */
export interface UnitStatusProp {
  kind: UnitStatusKind;
  untilTick: number | null;
  mag: number;
}

/** Unit prop `legend` on the champion's unit node (absent = not a champion). */
export interface LegendUnitProp {
  kind: LegendKind;
  level: number;
  soul: number;
  /** Remaining ticks per ability slot (index = slot); Dragon slot 0 = Firestorm. */
  cooldowns: [number, number, number];
}

/** Intent `summon_legend`: pay and start the summon project in a city. */
export interface SummonLegendIntent {
  playerIndex: number;
  /** `city_<n>` of an owned tier-3+ city containing the summoning site. */
  cityId: string;
}

/**
 * Intent `activate_legend_ability`. Slots: Dragon 0 = Firestorm; Golem 0 = Bulwark
 * (target an adjacent owned city hex to garrison, or the Golem's own hex to
 * fortify; again on its own hex to lift); Lich 0 = Withering, 1 = Ascend (target ignored).
 */
export interface ActivateLegendAbilityIntent {
  playerIndex: number;
  /** `unit_<n>` of the champion. */
  unitId: string;
  slot: number;
  targetQ: number;
  targetR: number;
}

/** Error codes returned in the intent error `message`. */
export type SummonRejection =
  | 'module_off'
  | 'game_over'
  | 'no_player'
  | 'not_a_city'
  | 'not_owner'
  | 'city_too_small'
  | 'no_summoning_site'
  | 'doctrine_locked'
  | 'not_available'
  | 'cannot_afford';

export type LegendAbilityRejection =
  | 'module_off'
  | 'not_a_legend'
  | 'not_owner'
  | 'no_such_slot'
  | 'on_cooldown'
  | 'out_of_range'
  /** Phase B: Bulwark target/state invalid, or Ascend not ready / already ascended. */
  | 'condition_unmet';

/** `legend:summon_started` event payload. */
export interface LegendSummonStartedEvent {
  playerIndex: number;
}

/** `legend:ability_activated` event payload. */
export interface LegendAbilityActivatedEvent {
  slot: number;
  /** Champion kind (`dragon` / `golem` / `lich`). */
  kind: LegendKind;
  /** Ability key (`firestorm` / `bulwark` / `withering` / `ascend`). */
  key: string;
  cooldownTicks: number;
  hits: number;
  kills: number;
  /** Target hex of the cast (for VFX). */
  targetQ: number;
  targetR: number;
}

/**
 * `legend:status_applied` event payload — a legend status landed on a unit.
 * `untilTick` follows `UnitStatusProp` (null = never expires); `key` is the
 * causing ability.
 */
export interface LegendStatusAppliedEvent {
  unitId: string;
  key: string;
  kind: UnitStatusKind;
  /** First tick on which the status is gone; null = never. */
  untilTick: number | null;
  mag: number;
}

/** `legend:status_expired` event payload — a legend status left a unit. */
export interface LegendStatusExpiredEvent {
  unitId: string;
  kind: UnitStatusKind;
}

/** One legend kind's balance row (`globals.legendRows.kinds[kind]`). */
export interface LegendKindRow {
  unitKind: string;
  siteBuilding: string;
  /** Doctrines that offer it; one needs >= `minDoctrineTier` points. */
  doctrines: string[];
  minDoctrineTier: number;
  minCityTier: number;
  gold: number;
  /** Mana (magic on) or faith (magic off). */
  rare: number;
  pop: number;
  summonTicks: number;
  abilityRange: number;
  /** Phase B (additive): ability rows by slot (index = slot). */
  abilities?: LegendAbilityRow[];
  /** Phase B (additive): doctrines for which this kind is the fixed default. */
  defaultFor?: string[];
}

/** One ability slot of a kind (`legendRows.kinds[k].abilities[slot]`). */
export interface LegendAbilityRow {
  /** 'firestorm' | 'bulwark' | 'withering' | 'ascend'. */
  key: string;
  shape: 'area_damage' | 'stance' | 'ascend';
  range: number;
  radius: number;
  /** Ticks charged on activation (a stance charges it when it ENDS; 0 on entering). */
  cooldown: number;
  /** [count, faces]; [0, 0] = no damage. */
  dice: [number, number];
  /** Status put on each unit hit, or null. */
  status: { kind: UnitStatusKind; ticks: number; mag: number } | null;
}

/** Snapshot `state.globals.legendRows` (present only while legends.enabled). */
export interface LegendRows {
  kinds: Record<string, LegendKindRow>;
  /** True when a summon's rare input is paid in mana (magic on), false = faith. */
  magicEnabled: boolean;
  /** `minCityTier` gates a summon only while `deaths <=` this (0 = first summon only). */
  cityTierGateMaxDeaths: number;
  /** Index = deaths so far; basis points of base gold/rare cost. */
  resummonCostBp: number[];
  /** Index = deaths (1-based; index 0 = 0). */
  respawnCooldownTicks: number[];
  /** Index = level - 1: total kills to reach that level. */
  killsForLevel: number[];
  maxLevel: number;
  /** Deaths at which level and kills reset. */
  resetDeaths: number;
  hpPerLevel: number;
  /** Phase B: how the offered kind is chosen. */
  offerRule?: 'dominant_doctrine';
  /** Phase B: Lich Soul / Ascend numbers. */
  soul?: { max: number; range: number; ascendAttackBonus: number; ascendHpBonus: number };
  firestorm: { cooldown: number; radius: number; dice: [number, number] };
}
